import Button from 'design-system/src/components/Button/Button';
import styled from 'styled-components';
import CheckCircleFilled from 'design-system/src/assets/icons/checkCircleFilled.svg?react';
import { Typography } from 'design-system/src/components/Typography/Typography';
import Hairline from 'design-system/src/components/Hairline/Hairline';
import { FormTopBar } from 'shared-utils/components/forms/FormTopBar';
import { Card } from 'design-system/src/components/Card/Card';
import { NAVBAR_HEIGHT } from 'design-system/src/components/TopNavbar/TopNavbar';
import { useFormFillerSuccessScreen } from './hooks';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  flex-shrink: 1;
  min-height: 0;
  width: 100%;
`;

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-height: calc(100vh - ${NAVBAR_HEIGHT}px);
  overflow: auto;
  background-color: var(--grayscale-50);
  flex-grow: 1;
  flex-shrink: 1;
  min-height: 0;
  width: 100%;
  padding-top: 120px;
  padding-bottom: 120px;
`;

const CardHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 24px;
  th {
    width: 33%;
    text-align: left;
  }
  td {
    width: 67%;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  width: 100%;
  flex: 1;
`;

export function SuccessScreen({ formName, downloadUrl }: { formName: string; downloadUrl: string }) {
  const [_shouldShowSuccessScreen, setShouldShowSuccessScreen] = useFormFillerSuccessScreen();
  const closeSuccessScreen = () => {
    setShouldShowSuccessScreen(false);
  };
  const currentDate = new Date().toLocaleDateString();
  return (
    <Container>
      <FormTopBar />
      <PageContainer>
        <Card desktopWidth="600px" variant="shadow" noGap>
          <CardHeader>
            <CheckCircleFilled width={64} height={64} color="var(--teal-500)" />
            <Typography styledAs="bodyMediumSpaceGrotesk" renderedAs="h1" weight={500}>
              You're all set!
            </Typography>
            <Typography styledAs="bodyLargeDMSans" renderedAs="p" weight={500}>
              You can download a copy below for your own records.
            </Typography>
          </CardHeader>
          <Hairline $marginTop="32px" $marginBottom="32px" />
          <Table>
            <tbody>
              <tr>
                <th>
                  <Typography styledAs="bodyLargeDMSans" renderedAs="p" weight={600}>
                    Form Name
                  </Typography>
                </th>
                <td>
                  <Typography styledAs="bodyLargeDMSans" renderedAs="p">
                    {formName}
                  </Typography>
                </td>
              </tr>
              <tr>
                <th>
                  <Typography styledAs="bodyLargeDMSans" renderedAs="p" weight={600}>
                    Date Submitted
                  </Typography>
                </th>
                <td>
                  {/* ignore this element in chromatic because the date changes every day triggering false positives */}
                  <Typography styledAs="bodyLargeDMSans" renderedAs="p" data-chromatic="ignore">
                    {currentDate}
                  </Typography>
                </td>
              </tr>
              <tr>
                <th>
                  <Typography styledAs="bodyLargeDMSans" renderedAs="p" weight={600}>
                    Completed Form
                  </Typography>
                </th>
                <td>
                  <Button href={downloadUrl} variant="link">
                    <Typography styledAs="bodyLargeDMSans" renderedAs="p">
                      Download (PDF)
                    </Typography>
                  </Button>
                </td>
              </tr>
            </tbody>
          </Table>
          <Hairline $marginTop="32px" $marginBottom="32px" />
          <ButtonContainer>
            <Button width="full" variant="secondary" onClick={closeSuccessScreen}>
              View Form
            </Button>
          </ButtonContainer>
        </Card>
      </PageContainer>
    </Container>
  );
}
